import { useEffect, useState } from "react";
import { Fingerprint, KeyRound, LogIn, ShieldCheck } from "lucide-react";
import {
  loadSavedCreds, clearCreds,
  hasPin, setPin as savePin, verifyPin, clearPin,
  bioAvailable, hasBio, registerBio, verifyBio, clearBio,
} from "@/utils/quickLogin";

/**
 * Quick unlock panel — shown on /login when remembered credentials exist.
 * Lets the user unlock via 4-digit PIN or platform biometric (Face/Touch ID),
 * then auto-submits the saved identifier + password.
 *
 * Props:
 *   onUnlock(creds)  — called with {identifier, password} on success.
 *   onFallback()     — user chose "use password" → parent should reveal form.
 */
export default function QuickLoginPanel({ onUnlock, onFallback }) {
  const creds = loadSavedCreds();
  const [pin, setPinInput] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [bioOk, setBioOk] = useState(false);
  const [mode, setMode] = useState(hasPin() ? "verify" : "setup"); // verify | setup | done
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");

  useEffect(() => { bioAvailable().then(setBioOk); }, []);

  if (!creds) return null;

  const maskedId = String(creds.identifier || "").replace(/(.{3}).*(.{2})/, "$1•••$2");

  const submitVerify = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const ok = await verifyPin(pin);
      if (!ok) { setErr("PIN غير صحيح"); return; }
      onUnlock(creds);
    } catch (ex) { setErr(ex.message || "خطأ"); }
    finally { setBusy(false); }
  };

  const submitSetup = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (newPin.length < 4) { setErr("PIN يجب أن يكون 4-6 أرقام"); return; }
      if (newPin !== newPin2) { setErr("رمزا PIN غير متطابقين"); return; }
      await savePin(newPin);
      setMode("done");
      onUnlock(creds); // proceed with login now that PIN is set
    } catch (ex) { setErr(ex.message || "خطأ"); }
    finally { setBusy(false); }
  };

  const doBio = async () => {
    setErr(""); setBusy(true);
    try {
      if (!hasBio()) {
        await registerBio(creds.identifier);
      } else {
        await verifyBio();
      }
      onUnlock(creds);
    } catch (ex) {
      setErr(ex.name === "NotAllowedError" ? "تم إلغاء البصمة" : (ex.message || "تعذّرت البصمة"));
    } finally { setBusy(false); }
  };

  const forget = () => {
    if (!window.confirm("نسيان هذا الجهاز؟ سيتم حذف البيانات المحفوظة و PIN والبصمة.")) return;
    clearCreds(); clearPin(); clearBio();
    onFallback();
  };

  return (
    <div className="bg-white rounded-3xl talabya-shadow-soft p-6 mb-4" data-testid="quick-login-panel">
      <div className="flex items-center gap-2 mb-1 text-primary-brand">
        <ShieldCheck size={18}/>
        <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>دخول سريع</h2>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-4">مرحباً بعودتك — {maskedId}</p>

      {mode === "verify" && (
        <form onSubmit={submitVerify} className="flex flex-col gap-3">
          <label className="text-xs font-bold text-[var(--text-secondary)]">أدخل رمز PIN للدخول</label>
          <input
            type="password" inputMode="numeric" maxLength={6} value={pin}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-center tracking-[8px] font-black text-2xl"
            placeholder="••••"
            autoFocus
            data-testid="quick-pin-input"/>
          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="quick-err">{err}</div>}
          <button type="submit" disabled={busy || pin.length < 4}
            className="w-full py-3 rounded-xl bg-primary-brand text-white font-bold text-base hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 talabya-shadow-float"
            data-testid="quick-pin-submit">
            <LogIn size={16}/> {busy ? "جارٍ..." : "دخول"}
          </button>
        </form>
      )}

      {mode === "setup" && (
        <form onSubmit={submitSetup} className="flex flex-col gap-3" data-testid="quick-pin-setup">
          <div className="text-xs bg-blue-50 text-blue-800 px-3 py-2 rounded-lg flex items-start gap-2">
            <KeyRound size={14} className="mt-0.5 shrink-0"/>
            أنشئ رمز PIN من 4-6 أرقام لتسريع الدخول من هذا الجهاز
          </div>
          <input
            type="password" inputMode="numeric" maxLength={6} value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-center tracking-[8px] font-black text-2xl"
            placeholder="أدخل PIN جديد"
            data-testid="quick-pin-new1"/>
          <input
            type="password" inputMode="numeric" maxLength={6} value={newPin2}
            onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-center tracking-[8px] font-black text-2xl"
            placeholder="أعد إدخاله للتأكيد"
            data-testid="quick-pin-new2"/>
          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</div>}
          <button type="submit" disabled={busy}
            className="w-full py-3 rounded-xl bg-primary-brand text-white font-bold text-base hover:opacity-90 disabled:opacity-60"
            data-testid="quick-pin-setup-submit">
            {busy ? "جارٍ الحفظ..." : "حفظ PIN ودخول"}
          </button>
        </form>
      )}

      {bioOk && mode === "verify" && (
        <button type="button" onClick={doBio} disabled={busy}
          className="mt-3 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-black flex items-center justify-center gap-2"
          data-testid="quick-bio-btn">
          <Fingerprint size={16}/> {hasBio() ? "دخول عبر البصمة" : "تفعيل البصمة والدخول"}
        </button>
      )}

      <div className="mt-4 flex items-center justify-between text-xs">
        <button type="button" onClick={onFallback} className="text-primary-brand font-bold hover:underline" data-testid="quick-use-password">
          استخدم كلمة المرور
        </button>
        <button type="button" onClick={forget} className="text-red-500 hover:underline" data-testid="quick-forget-device">
          نسيان هذا الجهاز
        </button>
      </div>
    </div>
  );
}
